import { useState } from 'react';
import { Alert, Button, Descriptions, Flex, Form, Input, Modal, Segmented, Select, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/api-context';
import { CATEGORY_LABEL, PURPOSE_LABEL, purposeOf, type VocabItem, type VocabPurpose } from '../../lib/vocab-types';

interface Values {
  slug: string;
  priority: number;
  keywords: string[];
  imageType: 'realistic' | 'character';
  rationale: string;
  imageSubject: string;
  purpose: VocabPurpose;
}

/** 바뀐 필드만 보낸다 */
export function diffPatch(v: VocabItem, values: Values): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  const slug = values.slug.trim() || null;
  if (slug !== v.slug) patch.slug = slug;
  if (values.priority !== v.priority) patch.priority = values.priority;
  if (JSON.stringify(values.keywords) !== JSON.stringify(v.keywords)) patch.keywords = values.keywords;
  if (values.imageType !== v.imageType) patch.imageType = values.imageType;
  if (values.rationale !== v.rationale) patch.rationale = values.rationale;
  const subject = values.imageSubject.trim() || null;
  if (subject !== (v.imageSubject ?? null)) patch.imageSubject = subject;
  if (values.purpose && values.purpose !== purposeOf(v)) patch.purpose = values.purpose;
  return patch;
}

const initialValues = (v: VocabItem): Values => ({ slug: v.slug ?? '', priority: v.priority, keywords: v.keywords, imageType: v.imageType, rationale: v.rationale, imageSubject: v.imageSubject ?? '', purpose: purposeOf(v) });

/**
 * 어휘 하나의 수정 양식. 폼 저장소(useForm)·저장 상태를 이 컴포넌트가 소유하고,
 * 부모가 어휘마다 key로 새로 만든다. 이전에 연 어휘의 입력값이 남지 않는다(#45 버그 수정).
 */
function VocabEditForm({ vocab, onClose }: { vocab: VocabItem; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [form] = Form.useForm<Values>();
  const [nothing, setNothing] = useState(false);
  const published = vocab.stats.published > 0 || vocab.stats.defaultPublished > 0 || vocab.stats.searchPublished > 0;

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.post<{ changed: string[] }>('/vocab/update', { vocabId: vocab.vocabId, patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vocab'] });
      onClose();
    },
  });

  const submit = async () => {
    let values: Values;
    try {
      values = await form.validateFields();
    } catch {
      return; // 입력 오류는 각 항목 아래에 표시된다
    }
    const patch = diffPatch(vocab, values);
    if (!Object.keys(patch).length) {
      setNothing(true);
      return;
    }
    setNothing(false);
    save.mutate(patch);
  };

  return (
    <>
      <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
        <Descriptions.Item label="카테고리">{CATEGORY_LABEL[vocab.category] ?? vocab.category}</Descriptions.Item>
        <Descriptions.Item label="구분">{vocab.vocabType === 'core' ? '핵심' : '주변'}</Descriptions.Item>
        <Descriptions.Item label="기존 카드">{vocab.existingCardId ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="출처">{vocab.sources.map((s) => s.sourceId).join(', ') || '-'}</Descriptions.Item>
      </Descriptions>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>표기·카테고리·핵심/주변·기존 카드는 확정 정책에 따라 여기서 바꾸지 않습니다.</Typography.Paragraph>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initialValues(vocab)}>
        <Form.Item
          name="slug"
          label="영문 파일명(슬러그)"
          extra={published ? '게시된 이미지가 있어 바꿀 수 없습니다.' : '검색 이미지 파일명 기준입니다. 예: ice_cream → images/food_object/ice_cream_01.webp'}
          rules={[{ pattern: /^$|^[a-z][a-z0-9_]*[a-z0-9]$/, message: '영문 소문자로 시작하고 영문 소문자·숫자·밑줄만 씁니다.' }, { pattern: /^$|^(?!.*_\d+$)/, message: '숫자 번호(_01 등)로 끝날 수 없습니다.' }]}
        >
          <Input placeholder="예: ice_cream" disabled={published} />
        </Form.Item>
        <Form.Item name="purpose" label="용도" extra="검색 이미지 전용: 픽사베이에 없는 한국 사물·음식처럼 검색용으로만 제공할 어휘(D-V8)">
          <Segmented options={(Object.keys(PURPOSE_LABEL) as VocabPurpose[]).map((p) => ({ value: p, label: PURPOSE_LABEL[p].label }))} />
        </Form.Item>
        <Form.Item name="priority" label="제작 우선순위">
          <Select options={[1, 2, 3, 4, 5].map((p) => ({ value: p, label: `${p}${p === 1 ? ' (높음)' : p === 5 ? ' (낮음)' : ''}` }))} />
        </Form.Item>
        <Form.Item name="keywords" label="동의어·검색 키워드" extra="입력 후 Enter. 저장할 때 소문자로 정리하고 대표 표기와 같은 키워드는 뺍니다.">
          <Select mode="tags" tokenSeparators={[',']} open={false} placeholder="예: 맘마" />
        </Form.Item>
        <Form.Item
          name="imageSubject"
          label="생성 피사체 설명"
          extra="이미지 생성 시 피사체로 우선 사용합니다. 한글·영문 모두 됩니다. 하나의 단위를 정확히 적어 주세요(예: 식빵 한 조각, 만 5세 아이의 오른손). 비우면 어휘 이름으로 생성합니다."
          rules={[{ pattern: /^[^"]*$/, message: '큰따옴표(")는 쓸 수 없습니다.' }, { max: 200, message: '200자 이하로 입력해 주세요.' }]}
        >
          <Input placeholder="예: 식빵 한 조각" />
        </Form.Item>
        <Form.Item name="imageType" label="이미지 제작 유형">
          <Segmented options={[{ value: 'realistic', label: '실사형' }, { value: 'character', label: '캐릭터형' }]} />
        </Form.Item>
        <Form.Item name="rationale" label="선정 근거">
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} />
        </Form.Item>
      </Form>
      {nothing && <Alert type="info" showIcon message="바뀐 내용이 없습니다." style={{ marginBottom: 12 }} />}
      {save.isError && <Alert type="error" showIcon message="저장하지 못했습니다" description={(save.error as Error).message} style={{ marginBottom: 12 }} />}
      <Flex justify="flex-end" gap={8}>
        <Button onClick={onClose}>취소</Button>
        <Button type="primary" loading={save.isPending} onClick={submit}>저장</Button>
      </Flex>
    </>
  );
}

export function VocabEditModal({ vocab, onClose }: { vocab: VocabItem | null; onClose: () => void }) {
  return (
    <Modal open={!!vocab} title={vocab ? `어휘 수정 · ${vocab.text} (${vocab.vocabId})` : ''} onCancel={onClose} footer={null}>
      {/* 어휘(와 그 최신 수정 시각)마다 양식을 새로 만든다 */}
      {vocab && <VocabEditForm key={`${vocab.vocabId}:${vocab.updatedAt}`} vocab={vocab} onClose={onClose} />}
    </Modal>
  );
}
